import { Anchor, Button, Group, Modal, MultiSelect, Select, Stack, Text } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCreatePageReport } from '../api/createReport';
import { useExportPageReportDialogs } from '../api/exportDialogs';
import { useGetPageReportPages } from '../api/getPages';
import { useGetPageReportTypes } from '../api/getTypes';
import { aiReportTypes } from '../types';
import { pagesSelectEmptyMessage, pagesSelectFieldError } from '../utils/pagesSelect';
import {
  STATUS_LABEL,
  defaultPeriod,
  formatDateTime,
  periodError,
  toIsoDay,
  utcToday,
} from '../utils/reportDocument';

/** Prefer same language, then done/empty over failed, then newest (DEV-3851). */
function pickExistingDuplicate(
  duplicates: aiReportTypes.Duplicate[],
  language: aiReportTypes.Language
): aiReportTypes.Duplicate | undefined {
  const rank = (status?: aiReportTypes.Status) => {
    if (status === 'done') return 0;
    if (status === 'empty') return 1;
    if (status === 'running' || status === 'queued') return 2;
    return 3;
  };
  const sorted = [...duplicates].sort((a, b) => {
    const langA = a.language === language ? 0 : 1;
    const langB = b.language === language ? 0 : 1;
    if (langA !== langB) return langA - langB;
    const statusDiff = rank(a.status) - rank(b.status);
    if (statusDiff !== 0) return statusDiff;
    return b.createdAt - a.createdAt;
  });
  return sorted[0];
}

type FormValues = {
  geo: aiReportTypes.Geo | null;
  source: aiReportTypes.Source | null;
  pageIds: string[];
  period: [Date | null, Date | null];
  promptKey: string | null;
  language: aiReportTypes.Language;
};

export const ReportForm = () => {
  const navigate = useNavigate();
  const [duplicates, setDuplicates] = useState<aiReportTypes.Duplicate[] | null>(null);
  const typesQuery = useGetPageReportTypes();
  const form = useForm<FormValues>({
    initialValues: {
      geo: null,
      source: null,
      pageIds: [],
      period: defaultPeriod(),
      promptKey: null,
      language: 'ru',
    },
    validate: {
      geo: (value) => (value ? null : 'Выберите гео'),
      source: (value) => (value ? null : 'Выберите источник'),
      pageIds: (value) => {
        if (!value.length) return 'Выберите страницу';
        if (value.length > 5) return 'Можно выбрать не больше 5 страниц';
        return null;
      },
      period: periodError,
      promptKey: (value) => (value ? null : 'Выберите тип отчёта'),
      language: (value) => (value ? null : 'Выберите язык'),
    },
  });

  const pagesQuery = useGetPageReportPages({ geo: form.values.geo, source: form.values.source });

  useEffect(() => {
    const first = typesQuery.data?.types[0];
    if (first && !form.values.promptKey) {
      form.setFieldValue('promptKey', first.key);
    }
  }, [typesQuery.data, form.values.promptKey, form]);

  const createReport = useCreatePageReport({
    config: {
      onSuccess: (data) => {
        setDuplicates(null);
        const summary = data.reports.find((report) => report.type === 'summary');
        navigate(`/ai-reports/${(summary ?? data.reports[0])._id}`);
      },
      onError: (error: AxiosError) => {
        const body = error.response?.data as { existing?: aiReportTypes.Duplicate[] } | undefined;
        if (error.response?.status === 409 && body?.existing) {
          setDuplicates(body.existing);
        }
      },
    },
  });

  const submit = (force?: boolean) => {
    const validation = form.validate();
    if (validation.hasErrors) return;
    const { geo, source, pageIds, period, promptKey, language } = form.values;
    if (!geo || !source || !promptKey || !period[0] || !period[1]) return;
    createReport.mutate({
      geo,
      source,
      pageIds,
      periodFrom: toIsoDay(period[0]),
      periodTo: toIsoDay(period[1]),
      promptKey,
      language,
      force,
    });
  };

  const exportDialogs = useExportPageReportDialogs();

  const isPagesDisabled = !form.values.geo || !form.values.source;
  const periodMessage = periodError(form.values.period);
  const hasPages = form.values.pageIds.length > 0 && form.values.pageIds.length <= 5;
  const canDownload =
    Boolean(form.values.geo && form.values.source) && hasPages && periodMessage === null;
  const isSubmittable = canDownload && Boolean(form.values.promptKey && form.values.language);

  const download = () => {
    const { geo, source, pageIds, period } = form.values;
    if (!canDownload || !geo || !source || !period[0] || !period[1]) return;
    exportDialogs.mutate({
      geo,
      source,
      pageIds,
      periodFrom: toIsoDay(period[0]),
      periodTo: toIsoDay(period[1]),
    });
  };
  const pageOptions = (pagesQuery.data?.pages ?? []).map((page) => ({
    value: page._id,
    label: page.isArchived ? `${page.name} (архив)` : page.name,
  }));

  return (
    <Stack gap="sm">
      <Group align="flex-start" grow>
        <Select
          label="Гео"
          placeholder="Лат.Ам или СНГ"
          data={[
            { value: 'latam', label: 'Лат.Ам' },
            { value: 'cis', label: 'СНГ' },
          ]}
          value={form.values.geo}
          error={form.errors.geo}
          onChange={(value) => {
            form.setFieldValue('geo', value as aiReportTypes.Geo | null);
            form.setFieldValue('pageIds', []);
          }}
        />
        <Select
          label="Источник"
          placeholder="База диалогов"
          data={[
            { value: 'telegram', label: 'Telegram' },
            { value: 'instagram', label: 'Instagram' },
            { value: 'facebook', label: 'Facebook' },
          ]}
          value={form.values.source}
          error={form.errors.source}
          onChange={(value) => {
            form.setFieldValue('source', value as aiReportTypes.Source | null);
            form.setFieldValue('pageIds', []);
          }}
        />
        <MultiSelect
          label="Страницы"
          placeholder={isPagesDisabled ? 'Сначала выберите гео и источник' : 'До 5 страниц'}
          data={pageOptions}
          value={form.values.pageIds}
          searchable
          maxValues={5}
          disabled={isPagesDisabled}
          description={
            pagesQuery.isError ? (
              <Anchor
                component="button"
                type="button"
                size="xs"
                onClick={() => pagesQuery.refetch()}
              >
                Повторить
              </Anchor>
            ) : undefined
          }
          error={pagesSelectFieldError(
            form.errors.pageIds ? String(form.errors.pageIds) : null,
            pagesQuery.isError
          )}
          nothingFoundMessage={pagesSelectEmptyMessage({
            isFetching: pagesQuery.isFetching,
            isError: pagesQuery.isError,
          })}
          onChange={(value) => form.setFieldValue('pageIds', value)}
        />
      </Group>
      <Group align="flex-start" grow>
        <DatePickerInput
          type="range"
          label="Период"
          placeholder="Не больше 14 дней"
          value={form.values.period}
          maxDate={utcToday()}
          valueFormat="DD.MM.YYYY"
          allowSingleDateInRange
          error={periodMessage}
          onChange={(value) => form.setFieldValue('period', value)}
        />
        <Select
          label="Тип отчёта"
          data={(typesQuery.data?.types ?? []).map((type) => ({
            value: type.key,
            label: type.name,
          }))}
          value={form.values.promptKey}
          error={form.errors.promptKey}
          onChange={(value) => form.setFieldValue('promptKey', value)}
        />
        <Select
          label="Язык отчёта"
          data={[
            { value: 'ru', label: 'RU' },
            { value: 'es', label: 'ES' },
          ]}
          value={form.values.language}
          error={form.errors.language}
          onChange={(value) =>
            form.setFieldValue('language', (value as aiReportTypes.Language) || 'ru')
          }
        />
      </Group>
      <Group>
        <Button
          loading={createReport.isLoading}
          disabled={!isSubmittable}
          onClick={() => submit(false)}
        >
          Сформировать отчёт
        </Button>
        <Button
          variant="default"
          loading={exportDialogs.isLoading}
          disabled={!canDownload}
          onClick={download}
        >
          Скачать диалоги JSON
        </Button>
      </Group>
      <Modal
        opened={Boolean(duplicates)}
        onClose={() => setDuplicates(null)}
        title="Отчёт за этот период уже есть"
      >
        <Stack gap="sm">
          {duplicates?.map((report) => (
            <Text key={report._id}>
              {report.pageName}: {formatDateTime(report.createdAt)}, {report.createdByName}
              {report.language ? `, ${report.language.toUpperCase()}` : ''}
              {report.status ? `, ${STATUS_LABEL[report.status]}` : ''}
            </Text>
          ))}
          <Group>
            <Button
              variant="default"
              onClick={() => {
                const existing = duplicates
                  ? pickExistingDuplicate(duplicates, form.values.language)
                  : undefined;
                if (existing) navigate(`/ai-reports/${existing._id}`);
              }}
            >
              Открыть существующий
            </Button>
            <Button
              loading={createReport.isLoading}
              onClick={() => {
                setDuplicates(null);
                submit(true);
              }}
            >
              Сформировать новый
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
};
